import { AnalyticsPlugin } from 'analytics';
import { camelCase } from 'lodash';

declare global {
  interface Window {
    FS: {
      getCurrentSessionURL(): string;
      identify: (userId: string, traits: Record<string, unknown>, source?: string) => void;
      setUserVars: (traits: Record<string, unknown>, source?: string) => void;
      event: (eventName: string, eventData?: Record<string, unknown>, source?: string) => void;
    };
  }
}

interface FullStoryConfig {
  org: string;
  debug: boolean;
}

interface AnalyticsPayload {
  userId?: string;
  anonymousId?: string;
  traits: Record<string, unknown>;
  event?: string;
  properties?: Record<string, unknown>;
}

interface PluginParams {
  payload: AnalyticsPayload;
  config: FullStoryConfig;
  options?: Record<string, unknown>;
}

const source = 'analytics';

export const fullstoryPlugin: AnalyticsPlugin = {
  name: 'fullstory',
  config: {},
  // https://help.fullstory.com/hc/en-us/articles/360020828113-FS-identify-Identifying-users
  // https://help.fullstory.com/hc/en-us/articles/360020623294-FS-setUserVars-Recording-custom-user-data
  identify: ({ payload }: PluginParams): boolean => {
    const { userId, anonymousId, traits } = payload;
    if (typeof window.FS === 'undefined') return false;
    const userTraits = formatPayload(traits, true);
    if (userId && typeof window.FS.identify === 'function') {
      window.FS.identify(userId, userTraits, source);
    } else if (anonymousId && typeof window.FS.setUserVars === 'function') {
      userTraits.analyticsAnonymousId_str = anonymousId;
      window.FS.setUserVars(userTraits, source);
    }
    return true;
  },
  /* https://help.fullstory.com/hc/en-us/articles/360020623234 */
  track: ({ payload }: PluginParams): boolean => {
    if (typeof window.FS === 'undefined') return false;
    const eventData = formatPayload(payload.properties || {});
    if (payload.event && typeof window.FS.event === 'function') {
      window.FS.event(payload.event, eventData, source);
    }
    return true;
  },
  loaded: (): boolean => {
    return !!window.FS;
  },
};

type FullStoryValue = string | number | boolean | Date | Array<string | number | boolean | Date>;

const suffixes = ['str', 'int', 'date', 'real', 'bool', 'strs', 'ints', 'dates', 'reals', 'bools'];
export function formatPayload(
  traits: Record<string, unknown>,
  ignoreReservedKeys = false
): Record<string, FullStoryValue> {
  return Object.keys(traits).reduce<Record<string, FullStoryValue>>((acc, curr) => {
    const value = traits[curr];
    if (ignoreReservedKeys && isSpecialKey(curr)) {
      acc[curr] = value as FullStoryValue;
      return acc;
    }

    const hasSuffix = suffixes.find((suffix) => curr.endsWith(`_${suffix}`));
    if (hasSuffix) {
      const cleanKey = curr.replace(`_${hasSuffix}`, '');
      acc[`${camelCase(cleanKey)}_${hasSuffix}`] = value as FullStoryValue;
      return acc;
    }

    const suffix = getSuffix(value);
    acc[`${camelCase(curr)}_${suffix}`] = value as FullStoryValue;
    return acc;
  }, {});
}

const specialKeys = ['displayName', 'email', 'uid', 'acctId', 'website'];

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function isSpecialKey(key: any) {
  return specialKeys.includes(key);
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function getSuffix(value: any) {
  if (Array.isArray(value)) {
    if (value.every((v) => typeof v === 'string')) {
      return 'strs';
    }
    if (value.every((v) => typeof v === 'boolean')) {
      return 'bools';
    }
    if (value.every((v) => typeof v === 'number')) {
      return isInt(value);
    }
    if (value.every((v) => v instanceof Date)) {
      return 'dates';
    }
  }
  if (value instanceof Date) {
    return 'date';
  }
  switch (typeof value) {
    case 'string':
      return 'str';
    case 'boolean':
      return 'bool';
    case 'number':
      return isInt(value);
    default:
      return 'str';
  }
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function isInt(value: any) {
  return value % 1 != 0 ? 'real' : 'int'; // eslint-disable-line
}
